/**
 * Email Verification Page
 *
 * This page is shown when users click the verification link in their email.
 * It verifies the token and updates the user's email verification status.
 */

import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Spinner, Button } from '@/components/ui';
import { CheckCircle, XCircle, Mail } from 'lucide-react';

type VerificationStatus = 'loading' | 'success' | 'error' | 'expired' | 'not-found';

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshEmailVerification, firebaseUser } = useAuth();

  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // Prevent multiple verification calls
  const verificationAttempted = useRef(false);

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyToken = async () => {
      // Prevent duplicate calls
      if (verificationAttempted.current) {
        return;
      }

      if (!token) {
        setStatus('not-found');
        setErrorMessage(t('auth.noToken', 'No se proporcionó token de verificación'));
        return;
      }

      // Mark as attempted before making the call
      verificationAttempted.current = true;

      try {
        const verifyEmailTokenFn = httpsCallable<{ token: string }, { success: boolean; message: string }>(
          functions,
          'verifyEmailToken'
        );

        const result = await verifyEmailTokenFn({ token });

        if (result.data.success) {
          setStatus('success');
          // Refresh the email verification status in the auth context
          if (firebaseUser) {
            await refreshEmailVerification();
          }
        } else {
          setStatus('error');
          setErrorMessage(result.data.message || t('auth.verificationFailed', 'Error al verificar el email'));
        }
      } catch (error: any) {
        console.error('Error verifying email:', error);

        // Handle specific error codes
        if (error.code === 'functions/deadline-exceeded') {
          setStatus('expired');
          setErrorMessage(t('auth.tokenExpired', 'El enlace de verificación ha expirado'));
        } else if (error.code === 'functions/not-found') {
          setStatus('not-found');
          setErrorMessage(t('auth.tokenNotFound', 'El enlace de verificación no es válido o ya fue utilizado'));
        } else {
          setStatus('error');
          setErrorMessage(error.message || t('auth.verificationFailed', 'Error al verificar el email'));
        }
      }
    };

    verifyToken();
  }, [token, firebaseUser, refreshEmailVerification, t]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-surface rounded-2xl shadow-xl p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-info/10 rounded-full flex items-center justify-center mb-6">
                <Spinner size="lg" />
              </div>
              <h1 className="text-2xl font-bold text-ink mb-3">
                {t('auth.verifyingEmail', 'Verificando tu email...')}
              </h1>
              <p className="text-ink-muted">
                {t('auth.pleaseWait', 'Por favor espera un momento')}
              </p>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-success" />
              </div>
              <h1 className="text-2xl font-bold text-ink mb-3">
                {t('auth.emailVerifiedTitle', 'Email verificado')}
              </h1>
              <p className="text-ink-muted">
                {t('auth.emailVerifiedMessage', 'Tu email ha sido verificado correctamente. Ya puedes acceder a todas las funcionalidades.')}
              </p>
            </div>

            <Button
              onClick={handleGoToDashboard}
              variant="primary"
              className="w-full"
            >
              {t('auth.goToDashboard', 'Ir al inicio')}
            </Button>
          </>
        )}

        {(status === 'error' || status === 'expired' || status === 'not-found') && (
          <>
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-danger/10 rounded-full flex items-center justify-center mb-6">
                {status === 'expired' ? (
                  <Mail className="w-10 h-10 text-danger" />
                ) : (
                  <XCircle className="w-10 h-10 text-danger" />
                )}
              </div>
              <h1 className="text-2xl font-bold text-ink mb-3">
                {status === 'expired'
                  ? t('auth.linkExpiredTitle', 'Enlace expirado')
                  : t('auth.verificationFailedTitle', 'Error de verificación')}
              </h1>
              <p className="text-ink-muted">
                {errorMessage}
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleGoToLogin}
                variant="primary"
                className="w-full"
              >
                {t('auth.goToLogin', 'Ir a iniciar sesión')}
              </Button>
              <p className="text-sm text-ink-subtle">
                {t('auth.requestNewLink', 'Puedes solicitar un nuevo enlace de verificación desde tu cuenta')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
